import React, { useState, useEffect, useMemo } from 'react'
import { isEmpty, set, uniq } from 'lodash/fp'
import { FormatService, useGetBusinessID, ViewAudienceModal } from '@marketing-milk/frontend'
import { PublishCampaignDTO, SavedAudience } from '@marketing-milk/interfaces'
import { StepProps } from 'components/Stepper'
import {
  getAudienceBoundsAverageTotal,
  getAudiencesById,
  getDefaultAudiences,
  isAudienceAvailable,
} from '../../helpers/campaignForm.helpers'
import { RenderWhenVisible } from '@marketing-milk/frontend'
import { fuzzySearch } from 'components/DataTable/helpers/search.helpers'
import { AudienceSpendLimitsModal } from 'pages/business/campaign-builder/steps/audience/audience-table/AudienceSpendLimitsModal'
import { Button } from '@mui/material'
import { AudiencesTable } from 'components/common-tables/audiences-table/AudiencesTable'
import { useRefreshAudienceAvailability } from 'hooks/audiences'

type Props = {
  campaignDto?: PublishCampaignDTO
  setCampaignDto: React.Dispatch<React.SetStateAction<PublishCampaignDTO | undefined>>
  audiences?: SavedAudience[]
} & StepProps

export const CampaignAdsetsStep = ({ audiences, campaignDto, setCampaignDto }: Props) => {
  const businessID = useGetBusinessID()
  const [spendLimits, setSpendLimits] = useState(campaignDto?.spendLimitsByAudienceId || {})
  const [searchTerm, setSearchTerm] = useState('')
  const [spendLimitsModalIsOpen, setSpendLimitsModalIsOpen] = useState(false)
  const [viewModalState, viewAudience] = useState<SavedAudience | undefined>()
  const refreshAvailability = useRefreshAudienceAvailability(businessID)

  // The DTO is the only place a selection lives. Deriving the rows from it means the checked rows
  // and the rendered rows are the same objects, so a refreshed audience list can't desync them.
  const selectedAudiences = useMemo(
    () => getAudiencesById(campaignDto?.audienceIDs, audiences),
    [campaignDto?.audienceIDs, audiences]
  )

  // Unavailable audiences are hidden rather than listed: on a business that switched ad accounts
  // they outnumber the usable ones several to one and push them onto a later page of the table.
  // One already on the draft stays visible — publish refuses it by name, so a hidden row would
  // leave the AE blocked by something they cannot see to remove.
  const visibleAudiences = useMemo(
    () =>
      audiences?.filter(
        audience => isAudienceAvailable(audience) || campaignDto?.audienceIDs?.includes(audience.id)
      ),
    [audiences, campaignDto?.audienceIDs]
  )

  // Counted against the whole list, not the search results — a count that moved while typing
  // would read as a filter rather than as what the business holds.
  const hiddenCount = (audiences?.length ?? 0) - (visibleAudiences?.length ?? 0)

  const onCheck: React.Dispatch<React.SetStateAction<SavedAudience[]>> = update => {
    const next = typeof update === 'function' ? update(selectedAudiences) : update
    const currentIDs = campaignDto?.audienceIDs ?? []
    // An unavailable audience already on the draft stays removable; it just can't be added.
    const nextIDs = uniq(
      next.filter(a => currentIDs.includes(a.id) || isAudienceAvailable(a)).map(({ id }) => id)
    )
    setCampaignDto(set('audienceIDs', nextIDs))
  }

  // Set spend limits in DTO when spend limits are selected
  useEffect(() => {
    setCampaignDto(set('spendLimitsByAudienceId', spendLimits))
  }, [spendLimits])

  // Pre-select the default audiences when nothing has been chosen yet
  useEffect(() => {
    if (!audiences) return
    if (!isEmpty(campaignDto?.audienceIDs)) return
    const defaultIDs = getDefaultAudiences(audiences).map(({ id }) => id)
    if (isEmpty(defaultIDs)) return
    setCampaignDto(set('audienceIDs', defaultIDs))
  }, [audiences])

  return (
    <div className="w-full flex flex-col p-6 space-y-3">
      <div className="flex space-x-4 items-center">
        <input
          className="bg-white rounded-md border px-4 py-3 flex-grow"
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Filter Audiences"
        />
        <Button
          onClick={() => refreshAvailability.mutate()}
          disabled={refreshAvailability.isLoading}
          variant="outlined"
          color="primary"
        >
          {refreshAvailability.isLoading ? 'Re-syncing...' : 'Re-sync Audiences'}
        </Button>
        <Button
          onClick={() => setSpendLimitsModalIsOpen(v => !v)}
          variant="outlined"
          color="primary"
        >
          Set Spend Limits
        </Button>
      </div>

      {/* The one number on this screen that moves as boxes are ticked, so it is the only thing
          here weighted like a figure. */}
      <div className="flex items-baseline gap-2">
        <span className="text-sm text-mm-neutral-1-600">Audience size</span>
        <span className="text-lg font-bold text-mm-neutral-1-900">
          {FormatService.getValue(getAudienceBoundsAverageTotal(selectedAudiences)) || 0}
        </span>
        <span className="text-sm text-mm-neutral-1-600">
          across {selectedAudiences.length}{' '}
          {selectedAudiences.length === 1 ? 'audience' : 'audiences'}
        </span>
      </div>

      <AudienceSpendLimitsModal
        isOpen={spendLimitsModalIsOpen}
        onClose={() => setSpendLimitsModalIsOpen(false)}
        audiences={selectedAudiences!}
        spendLimits={spendLimits!}
        setSpendLimits={setSpendLimits!}
      />

      {hiddenCount > 0 && (
        <small className="text-mm-neutral-1-600">
          {hiddenCount} {hiddenCount === 1 ? 'audience is' : 'audiences are'} hidden because{' '}
          {hiddenCount === 1 ? 'it is' : 'they are'} not available in the connected ad account.{' '}
          {hiddenCount === 1 ? 'It remains' : 'They remain'} in the Audiences library.
        </small>
      )}

      <div>
        <RenderWhenVisible>
          {visibleAudiences && (
            <AudiencesTable
              audiences={fuzzySearch(visibleAudiences, searchTerm)}
              emptyMessage={searchTerm ? `No audiences match “${searchTerm}”` : 'No audiences yet'}
              checkedAudiences={selectedAudiences}
              onCheck={onCheck}
              markUnavailable
              recalcAudience={() => {}}
              viewAudience={viewAudience}
            />
          )}
        </RenderWhenVisible>
      </div>

      {viewModalState && (
        <ViewAudienceModal
          onHideModal={() => viewAudience(undefined)}
          onRecalc={() => {}}
          showModal={!!viewModalState}
          audiences={[viewModalState]}
        />
      )}
    </div>
  )
}
